import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Gift, Zap, Flame, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { claimDaily, claimMissions } from "../lib/api";

export default function DailyCheckin({ status, onRefresh }) {
  if (!status) return null;
  const streak = status.streak || 0;
  const claimed = status.daily_claimed_today;
  const missionsClaimed = status.missions_claimed_today;
  const doneCount = (status.missions || []).filter(m => m.done).length;
  const allDone = doneCount === (status.missions || []).length;

  const doClaim = async () => {
    try {
      const r = await claimDaily();
      toast.success(`+${r.credits_added.toLocaleString("en-IN")} credits`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Already claimed");
    }
  };
  const doMissions = async () => {
    try {
      const r = await claimMissions();
      toast.success(`+${r.credits_added.toLocaleString("en-IN")} credits from missions`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nothing to claim yet");
    }
  };

  return (
    <section data-testid="daily-checkin" className="glass-strong rounded-3xl p-5 relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full opacity-40" style={{ background: "radial-gradient(circle,#FFD60066,transparent 70%)" }} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-white/50">Daily Check-in</p>
          <h3 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
            <Flame className="w-6 h-6 text-[#FF0055]" /> {streak} day{streak !== 1 ? "s" : ""} streak
          </h3>
          <p className="text-xs text-white/60 mt-1">Keep your streak alive for bigger Fanpuff Credit bonuses.</p>
        </div>
        <button
          data-testid="daily-claim-btn"
          onClick={doClaim}
          disabled={claimed}
          className="px-4 py-2 rounded-full text-sm font-bold text-black disabled:opacity-60"
          style={{ background: claimed ? "rgba(255,255,255,0.12)" : "linear-gradient(90deg,#00F0FF,#B026FF)", color: claimed ? "#fff" : "#000" }}
        >
          {claimed ? "Claimed" : "Claim +2,000"}
        </button>
      </div>

      {/* Streak dots */}
      <div className="mt-4 flex items-center gap-1.5">
        {Array.from({ length: 7 }).map((_, i) => {
          const active = i < (streak % 8 === 0 ? 7 : streak % 8);
          return <span key={i} className="flex-1 h-1.5 rounded-full" style={{ background: active ? "linear-gradient(90deg,#B026FF,#00F0FF)" : "rgba(255,255,255,0.08)" }} />;
        })}
      </div>

      {/* Missions */}
      <div className="mt-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50">Today's missions · {doneCount}/{(status.missions || []).length}</p>
          <button
            onClick={doMissions}
            disabled={missionsClaimed || doneCount === 0}
            data-testid="missions-claim-btn"
            className="text-xs font-bold inline-flex items-center gap-1 px-3 py-1 rounded-full disabled:opacity-50"
            style={missionsClaimed ? { background: "rgba(255,255,255,0.08)", color: "#fff" } : { background: "#00FF6622", color: "#00FF66", border: "1px solid #00FF6655" }}
          >
            <Gift className="w-3 h-3" /> {missionsClaimed ? "Claimed" : "Collect"}
          </button>
        </div>
        <div className="mt-2 space-y-1.5">
          {(status.missions || []).map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="flex items-center gap-2 glass rounded-xl px-3 py-2"
              data-testid={`mission-${m.id}`}
            >
              <span className={`w-6 h-6 rounded-full grid place-items-center ${m.done ? "" : "border border-white/15"}`} style={m.done ? { background: "#00FF66" } : {}}>
                {m.done && <CheckCircle2 className="w-4 h-4 text-black" />}
              </span>
              <span className={`flex-1 text-sm ${m.done ? "text-white/50 line-through" : "text-white/85"}`}>{m.label}</span>
              <span className="chip" style={{ background: "#FFD60018", color: "#FFD600", border: "1px solid #FFD60044" }}>
                <Zap className="w-3 h-3" /> +{(m.credits || 0).toLocaleString("en-IN")}
              </span>
            </motion.div>
          ))}
        </div>
        {allDone && !missionsClaimed && (
          <p className="mt-2 text-xs text-[#00FF66]">Bonus +3,000 credits available for completing all missions.</p>
        )}
      </div>
    </section>
  );
}
